/* The 3D scatter and 3D bubble charts' slice of the shared XYZ source data.
 *
 * One builder for both, because the two differ only in whether the radius field varies mark area -
 * which is a rendering choice the view makes, not a different set of points. Neither grids
 * anything, so neither needs three positions to draw: one component is one mark. */

import type {
  APIComponentType,
  ArdoqId,
  FormattingFilter,
} from '@ardoq/api-types';
import { groupBy, uniq } from 'lodash';
import type {
  EnhancedScopeData,
  FormattingAndLabelRules,
} from '@ardoq/data-model';
import {
  enhancedScopeDataOperations,
  type FilterColorData,
} from '@ardoq/scope-data';
import type { PlottedContext } from './plottedContext';
import type { PermissionContext } from '@ardoq/access-control';
import { type Locale } from '@ardoq/locale';
import { type I18nLocale } from '@ardoq/i18n';
import {
  getLegendComponentTypeColor,
  type LegendComponentType,
  type LoadedGraphWithViewpointMode,
} from '@ardoq/graph';
import {
  getActiveConditionalFormattingForLegendPure,
  type ActiveConditionalFormattingForLegend,
} from '@ardoq/view-legend';
import {
  type XyzFieldSelection,
  type RequiredDimension,
  type XyzDataPoint,
  type HorizontalAxes,
  toNiceHorizontalAxes,
} from '@ardoq/xyz-charts';
import type { ChartEmptyReason, NumericField } from 'tabview/chartShared/types';
import { toFieldLabel } from 'tabview/chartShared/fieldLabel';
import { buildXyzPoints } from './buildXyzPoints';

/**
 * One mark is a chart.
 *
 * The gridded charts need three distinct positions to have an area to interpolate over; a point
 * chart interpolates nothing. This is why a 1-5 score field is a dead end for a surface and
 * perfectly readable here, and why the surface's empty state points at this view by name.
 */
const POINTS_MINIMUM_POSITIONS = 1;

export type Points3DSourceDataArgs = {
  context: PlottedContext;
  /** The only part of the settings that can move a point - see `ContourSourceDataArgs`. */
  viewSettings: XyzFieldSelection;
  loadedGraph: LoadedGraphWithViewpointMode;
  permissionContext: PermissionContext;
  locale: Locale;
  i18nLocale: I18nLocale;
  formattingAndLabelRules: FormattingAndLabelRules;
  filterColorData: FilterColorData;
};

export type Points3DLegendData = {
  /** The types of the plotted components only, so the legend names nothing the picture lacks. */
  componentTypes: LegendComponentType[];
  activeConditionalFormatting: ActiveConditionalFormattingForLegend;
};

export type Points3DSourceData = {
  viewInstanceId: string;
  points: XyzDataPoint[];
  legend: Points3DLegendData;
  /**
   * The plane the chart is drawn against, decided here rather than in the chart.
   *
   * Rounded outward to the numbers a reader would name, per axis. The rest of the derivation
   * lives in this layer, and the four XYZ views can only be made to agree about their shared
   * plane if one place decides it.
   */
  axes: HorizontalAxes;
  excludedByDimension: Record<RequiredDimension, number>;
  excludedCount: number;
  numericFields: NumericField[];
  xAxisTitle: string;
  yAxisTitle: string;
  zAxisTitle: string;
  /**
   * The field varying mark area, for the bubble chart to name.
   *
   * Empty when nothing is picked, like the other three. Only the bubble reads it - on a scatter
   * there is no size channel for it to describe.
   */
  radiusAxisTitle: string;
  isViewEmpty: boolean;
  emptyReason: ChartEmptyReason;
  locale: Locale;
  i18nLocale: I18nLocale;
};

type TypedComponent = {
  componentId: ArdoqId;
  componentType: APIComponentType;
};

const toLegendComponentType = (
  componentType: APIComponentType,
  ids: ArdoqId[]
): LegendComponentType => ({
  name: componentType.name,
  icon: componentType.icon,
  image: componentType.image,
  ids,
  backgroundColor: getLegendComponentTypeColor(componentType),
});

/** Keyed by name, as the 3D network's legend is: two workspaces sharing a type name share a row. */
export const toLegendComponentTypes = (
  enhancedScopeData: EnhancedScopeData,
  points: XyzDataPoint[]
): LegendComponentType[] => {
  const typedComponents = uniq(
    points.map(({ componentId }) => componentId)
  ).flatMap((componentId): TypedComponent[] => {
    const componentType: APIComponentType | undefined =
      enhancedScopeDataOperations.getComponentTypeByComponentId(
        enhancedScopeData,
        componentId
      );
    return componentType ? [{ componentId, componentType }] : [];
  });

  return Object.values(
    groupBy(typedComponents, ({ componentType }) => componentType.name)
  ).map(group =>
    toLegendComponentType(
      group[0].componentType,
      group.map(({ componentId }) => componentId)
    )
  );
};

const isComponentColorRule = (
  rule: FormattingAndLabelRules['rules'][number]
): rule is FormattingFilter =>
  'color' in rule && Boolean(rule.color) && Boolean(rule.affectComponent);

export const buildPoints3DSourceData = (
  viewInstanceId: string,
  {
    context,
    viewSettings,
    loadedGraph,
    permissionContext,
    locale,
    i18nLocale,
    formattingAndLabelRules,
    filterColorData,
  }: Points3DSourceDataArgs
): Points3DSourceData => {
  const {
    points,
    excludedByDimension,
    excludedCount,
    numericFields,
    emptyReason,
  } = buildXyzPoints({
    context,
    loadedGraph,
    permissionContext,
    i18nLocale,
    formattingAndLabelRules,
    filterColorData,
    fieldSelection: viewSettings,
    minimumDistinctPositions: POINTS_MINIMUM_POSITIONS,
  });

  return {
    viewInstanceId,
    numericFields,
    xAxisTitle: toFieldLabel(numericFields, viewSettings.selectedFieldNameX),
    yAxisTitle: toFieldLabel(numericFields, viewSettings.selectedFieldNameY),
    zAxisTitle: toFieldLabel(numericFields, viewSettings.selectedFieldNameZ),
    radiusAxisTitle: toFieldLabel(
      numericFields,
      viewSettings.selectedFieldNameRadius
    ),
    locale,
    i18nLocale,
    points,
    legend: {
      componentTypes: toLegendComponentTypes(loadedGraph.scopeData, points),
      activeConditionalFormatting: getActiveConditionalFormattingForLegendPure(
        loadedGraph.scopeData,
        formattingAndLabelRules.rules.filter(isComponentColorRule),
        i18nLocale,
        true
      ),
    },
    /* Off the plotted points, which is the population the chart draws. */
    axes: toNiceHorizontalAxes(points),
    excludedByDimension,
    excludedCount,
    isViewEmpty: emptyReason !== null,
    emptyReason,
  };
};
